<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { Button, Dialog, Select } from 'primevue';
import { DashboardComponentType } from 'shared/dashboards/domain/dashboardComponentType';
import ProblemDetailsView from 'shared/problems/components/ProblemDetailsView.vue';
import type { ProblemDetails } from 'shared/problems/domain/problemDetails';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { computed, onMounted, ref } from 'vue';
import { type DashboardComponent } from '../domain/dashboard';
import { useListDashboardComponentTypeConfigApi } from '../api/listDashboardComponentTypeConfigApi';
import type { DashboardComponentTypeConfigResponse } from '../models/DashboardComponentTypeConfigResponse';

const visible = ref(false);
const problems = ref<ProblemDetails>();

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);

const current = ref<DashboardComponent | null>();

//ta in de komponenttyper som är aktiverade/enabled
const enabledDashboardComponentTypes = ref<DashboardComponentTypeConfigResponse[]>([]);
const listDashboardComponentTypeConfigApi = useListDashboardComponentTypeConfigApi();

//hämta in komponenttyperna vid inladdning
onMounted(async () => {
  enabledDashboardComponentTypes.value = await listDashboardComponentTypeConfigApi.execute();
});

defineExpose({
  show,
});

const emit = defineEmits<{
  confirm: [component: DashboardComponent];
}>();

const typeOptions = computed<TypeOption[]>(() => {
  return (
    enabledDashboardComponentTypes.value
      //plockar ut de som är enabled och inte undefined som typ
      .filter((t) => t.enabled && t.dashboardComponentType !== undefined)
      //bygger nytt objekt med de delar som behövs
      .map((t) => {
        return {
          value: t.dashboardComponentType,
          name: translations.value.enums.dashboardComponentType(t.dashboardComponentType),
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
  );
});

const selectedType = ref<TypeOption>(typeOptions.value[0]!);
const selectedTypeChanged = computed(() => {
  if (!current.value) return false;
  return selectedType.value.value !== current.value.type;
});

const confirmButtonLabel = computed(() =>
  current.value ? translations.value.generic.edit : translations.value.generic.add,
);

interface TypeOption {
  value: DashboardComponentType;
  name: string;
}

interface ShowOptions {
  component: DashboardComponent | null;
}

function show(options: ShowOptions) {
  current.value = options.component;
  selectedType.value =
    typeOptions.value.find((o) => o.value === options.component?.type) ?? typeOptions.value[0]!;
  visible.value = true;
}

function onConfirmClick() {
  if (current.value) {
    current.value.type = selectedType.value.value;
  } else {
    const component: DashboardComponent = {
      id: crypto.randomUUID(),
      x: 0,
      y: 0,
      width: 12,
      height: 1,
      type: selectedType.value.value,
    };

    emit('confirm', component);
  }

  visible.value = false;
}
</script>

<template>
  <Dialog
    class="edit-dashboard-component-dialog"
    :header="
      current
        ? translations.dashboard.editComponentDialog.editTitle
        : translations.dashboard.editComponentDialog.addTitle
    "
    v-model:visible="visible"
  >
    <ProblemDetailsView v-model="problems"></ProblemDetailsView>
    <div class="component-form">
      <div class="field" :class="{ changed: selectedTypeChanged }">
        <label for="edit-dashboard-component-type">{{ translations.generic.type }}</label>
        <Select
          autofocus
          autocomplete="off"
          id="edit-dashboard-component-type"
          name="type"
          :options="typeOptions"
          optionLabel="name"
          v-model="selectedType"
        ></Select>
      </div>
    </div>
    <template #footer>
      <Button :label="confirmButtonLabel" @click="onConfirmClick"></Button>
    </template>
  </Dialog>
</template>

<style scoped>
.component-form {
  display: grid;
  row-gap: 0.5rem;
  margin-bottom: 0.5rem;
}
</style>

<style>
.edit-dashboard-component-dialog {
  min-width: 300px;
}
</style>
